import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { resolveCountryCodeByLanguage } from '../../../shared/config';
import {
  formatAmountValue,
  normalizeNamePart,
  resolveCurrencyPrefix,
} from '../../../shared/model';
import { PrimaryButton, TipFlowTopBar } from '../../../shared/ui';

import {
  RewardAmountBurst,
  RewardHeartArrowGraphic,
} from './tip-reward-claim-screen.graphics';
import {
  AccountLink,
  Actions,
  AvatarFrame,
  AvatarImage,
  Journey,
  JourneyConnector,
  JourneyLabel,
  RewardAmountCluster,
  RewardLabel,
  RewardAmountPlus,
  RewardAmountText,
  RewardAmountValue,
  RewardStage,
  Screen,
  StepAccountIcon,
  StepDonationIcon,
  StepGiftIcon,
  StepGiftIconWrap,
  StepGiftShell,
  Title,
  TitleLead,
  TitleRest,
} from './tip-reward-claim-screen.styles';

type TipRewardClaimScreenProps = {
  isActionsDisabled?: boolean;
  onClaimGift: () => void;
  onOpenAccount: () => void;
  recipientFirstName?: string | null;
  rewardAmount?: number | null;
};

export function TipRewardClaimScreen({
  isActionsDisabled = false,
  onClaimGift,
  onOpenAccount,
  recipientFirstName,
  rewardAmount,
}: TipRewardClaimScreenProps) {
  const { i18n, t } = useTranslation();
  const titleParts = useMemo(() => {
    const normalizedFirstName = normalizeNamePart(recipientFirstName ?? '');

    if (normalizedFirstName.length > 0) {
      return {
        lead: t('tipsRewardClaim.titleLead'),
        rest: t('tipsRewardClaim.titleTail', {
          name: normalizedFirstName,
        }),
      };
    }

    return {
      lead: t('tipsRewardClaim.titleLead'),
      rest: t('tipsRewardClaim.titleTailFallback'),
    };
  }, [recipientFirstName, t]);
  const rewardAmountContent = useMemo(() => {
    if (
      rewardAmount === null ||
      rewardAmount === undefined ||
      !Number.isFinite(rewardAmount) ||
      rewardAmount < 0
    ) {
      return null;
    }

    const normalizedRewardAmount = Math.floor(rewardAmount);
    const countryCode = resolveCountryCodeByLanguage(i18n.language);
    const currencyPrefix = resolveCurrencyPrefix(countryCode).trimEnd();
    const amountValue = formatAmountValue(normalizedRewardAmount, countryCode);

    return {
      ariaLabel: t('tipsRewardClaim.rewardAmountAria', {
        amount: `${currencyPrefix} ${amountValue}`,
      }),
      amountValue,
    };
  }, [i18n.language, rewardAmount, t]);

  return (
    <Screen>
      <TipFlowTopBar />
      <Journey>
        <StepDonationIcon />
        <JourneyConnector $isCompleted $left={29} $width={142} />
        <StepAccountIcon />
        <JourneyConnector $left={167} $width={126} />
        <StepGiftIconWrap>
          <StepGiftShell />
          <StepGiftIcon />
        </StepGiftIconWrap>

        <JourneyLabel $left={-11} $tone="completed" $width={80}>
          {t('tipsRewardClaim.stepDonation')}
        </JourneyLabel>
        <JourneyLabel $left={139} $tone="active" $width={69}>
          {t('tipsRewardClaim.stepAccount')}
        </JourneyLabel>
        <JourneyLabel $left={271} $tone="upcoming" $width={90}>
          {t('tipsRewardClaim.stepGift')}
        </JourneyLabel>
      </Journey>

      <Title>
        <TitleLead>{titleParts.lead}</TitleLead>{' '}
        <TitleRest>{titleParts.rest}</TitleRest>
      </Title>

      <RewardStage>
        <AvatarFrame>
          <AvatarImage />
        </AvatarFrame>
        <RewardLabel>{t('tipsRewardClaim.rewardLabel')}</RewardLabel>
        {rewardAmountContent !== null ? (
          <RewardAmountCluster
            aria-label={rewardAmountContent.ariaLabel}
            role="img"
          >
            <RewardAmountBurst />
            <RewardAmountText>
              <RewardAmountPlus>+</RewardAmountPlus>
              <RewardAmountValue>
                {rewardAmountContent.amountValue}
              </RewardAmountValue>
            </RewardAmountText>
          </RewardAmountCluster>
        ) : null}
        <RewardHeartArrowGraphic />
      </RewardStage>

      <Actions>
        <PrimaryButton
          disabled={isActionsDisabled}
          type="button"
          onClick={onClaimGift}
        >
          {t('tipsRewardClaim.claimCta')}
        </PrimaryButton>
        <AccountLink
          disabled={isActionsDisabled}
          type="button"
          onClick={onOpenAccount}
        >
          {t('tipsRewardClaim.accountLink')}
        </AccountLink>
      </Actions>
    </Screen>
  );
}
